import { gql } from '@apollo/client'
import { CORE_ROLE_FIELDS } from './roles'
import { CORE_STORE_FIELDS, STORE_SETTINGS_FIELDS } from './stores'
import { CORE_SHIFT_FIELDS } from './shifts'

const CORE_CLIENT_FIELDS = gql`
  fragment ClientFields on Client {
    id
    name
    isInternal
    meta
    settings {
      clientId
      useOutTheDoorPricing
      deactivationAt
      loyaltyProvider
      calculateOtdDiscountsPreTax
      isDepositsEnabled
    }
  }
`

export const CORE_OWN_USER_FIELDS = gql`
  ${CORE_ROLE_FIELDS}
  ${CORE_STORE_FIELDS}
  ${CORE_SHIFT_FIELDS}
  ${CORE_CLIENT_FIELDS}
  fragment OwnUserFields on OwnUser {
    id
    clientId
    isLoggedIn
    activeStoreId
    email
    phoneNumber
    isInternal
    status
    hasViewAccess
    meta
    apiKeys {
      key
      state
      username
      password
      fields {
        key
        value
      }
    }
    stores {
      ...StoreFields
    }
    roleId
    canSetPin
    role {
      ...RoleFields
    }
    client {
      ...ClientFields
    }
    activeStore {
      ...StoreFields
      settings {
        regulatorId
      }
    }
    carts {
      id
      customerId
      storeId
      cartType
      status
      checkedInBy
      openedBy
      checkedInAt
      openedAt
    }
    activeDrawers {
      id
      name
      clientId
      storeId
      type
      openedAt
      closedAt
      openedBy
      closedBy
    }
    files {
      userFileId
      userId
      name
      url
    }
    currentShift {
      ...ShiftFields
    }
    preferences {
      theme
    }
  }
`

/** Slim boot read for FhThemeProvider — OwnUser.preferences also rides GET_OWN_USER. */
export const OWN_USER_THEME_PREFERENCE = gql`
  query OwnUserThemePreference {
    ownUser {
      id
      preferences {
        theme
      }
    }
  }
`

export const SAVE_USER_PREFERENCES = gql`
  mutation SaveUserPreferences($input: UserPreferencesUpdate!) {
    saveUserPreferences(input: $input) {
      theme
    }
  }
`

export const USER_ID = gql`
  query OwnUserId {
    ownUser {
      id
    }
  }
`

export const USER_CLIENT_SETTINGS = gql`
  query OwnUserClient {
    ownUser {
      id
      client {
        id
        meta
        settings {
          clientId
          useOutTheDoorPricing
          loyaltyProvider
          loyaltyPointsPerDollar
          hasCrmEnabled
          deactivationAt
          billingNoticeTemplateId
          billingNoticeMerge
          billingNoticeAudience
          billingNoticeActive
          calculateOtdDiscountsPreTax
          isDepositsEnabled
          supportEmail
        }
      }
    }
  }
`

// Every role reads this, cashiers included. The per-user Text status needs
// marketing read, so it loads separately (MARKETING_TEXTING_STATUS) and a
// refused Text status can't take the entitlements down with it.
export const OWN_USER_MARKETING_ENTITLEMENTS = gql`
  query OwnUserMarketingEntitlements {
    ownUser {
      id
      client {
        id
        marketingEntitlements {
          marketingSuiteEnabled
          emailEnabled
          passesEnabled
          textEnabled
          textActive
          textRequested
          provisioning {
            sendingDomain
            sendingAddress
          }
        }
      }
    }
  }
`

export const SET_OWN_ACTIVE_STORE = gql`
  mutation SetOwnActiveStore($storeId: ID!) {
    setOwnActiveStore(storeId: $storeId)
  }
`

export const SET_OWN_CLIENT = gql`
  mutation SetOwnClient($clientId: ID!) {
    setOwnClient(clientId: $clientId)
  }
`

export const ACTIVE_STORE_META = gql`
  query OwnUserActiveStoreMeta {
    ownUser {
      id
      activeStore {
        id
        meta
      }
    }
  }
`

// Its own query, so a store whose Text setup can't be read never blanks the rest
// of the register.
export const ACTIVE_STORE_TEXT_SETUP = gql`
  query OwnUserActiveStoreTextSetup {
    ownUser {
      id
      activeStore {
        id
        textSetup {
          state
          phoneNumber
        }
      }
    }
  }
`

export const ACTIVE_STORE_SETTINGS = gql`
  ${STORE_SETTINGS_FIELDS}
  query OwnUserActiveStoreSettings {
    ownUser {
      id
      activeStore {
        id
        settings {
          ...StoreSettingsFields
        }
      }
    }
  }
`

export const OWN_USER_STORES = gql`
  ${STORE_SETTINGS_FIELDS}
  query OwnUserStores {
    ownUser {
      id
      activeStoreId
      stores {
        id
        name
        city
        state
        address
        settings {
          ...StoreSettingsFields
        }
      }
      client {
        id
        name
        stores {
          id
        }
      }
      activeStore {
        id
        name
        city
        state
      }
      meta
    }
  }
`

export const OWN_USER_STORE_SETTINGS = gql`
  ${STORE_SETTINGS_FIELDS}
  query OwnUserStoreSettings {
    ownUser {
      id
      activeStore {
        id
        state
        settings {
          ...StoreSettingsFields
        }
      }
      stores {
        id
        name
        state
        settings {
          ...StoreSettingsFields
        }
      }
    }
  }
`

export const ACTIVE_STORE_LICENSES_STATE = gql`
  query ActiveStoreState {
    ownUser {
      id
      activeStore {
        id
        licenses {
          licenseType
        }
        state
        settings {
          storeId
          medCustomersCanPurchaseRecInventoryCheckedInAsMed
        }
      }
    }
  }
`

export const ACTIVE_DRAWERS = gql`
  query ActiveDrawers {
    ownUser {
      id
      activeDrawers {
        id
        name
        type
      }
    }
  }
`

export const GET_OWN_USER = gql`
  ${CORE_OWN_USER_FIELDS}
  query GetOwnUser {
    ownUser {
      ...OwnUserFields
    }
  }
`

export const GET_OWN_USER_META = gql`
  query GetOwnUserMeta {
    ownUser {
      meta
      id
    }
  }
`

export const UPDATE_OWN_USER = gql`
  ${CORE_OWN_USER_FIELDS}
  mutation UpdateOwnUser(
    $apiKeys: [NewApiKey]
    $pin: String
    $meta: String
    $roleId: ID
    $hasViewAccess: Boolean
    $phoneNumber: String
  ) {
    updateOwnUser(
      user: {
        apiKeys: $apiKeys
        pin: $pin
        meta: $meta
        roleId: $roleId
        hasViewAccess: $hasViewAccess
        phoneNumber: $phoneNumber
      }
    ) {
      ...OwnUserFields
    }
  }
`
